/**
 * ActorAIService
 *
 * Service for creating actors via AI webhook.
 * Handles payload building, webhook calls, and error handling.
 *
 * Features:
 * - Build complete source data from hydrated selections
 * - Build webhook payload with all required fields
 * - POST to backend proxy with proper error handling
 * - Strip unnecessary fields (fullInterviewResult, createdAt)
 */

import { getBackendUrl, getCurrentEnvironment } from '../../../../frontend/src/config/backend';
import { WebhookSettingsService } from '@modules/firebase-core/services/WebhookSettingsService';
import type {
  ActorAIRequest,
  ActorAIResponse,
  CompleteSourceData,
  HydratedSourceData,
  Audience,
  ProjectData,
  WebhookProxyRequest,
} from '../types';

// =============================================================================
// TYPES
// =============================================================================

/**
 * Result from service operations
 */
export interface ActorAIServiceResult {
  success: boolean;
  data?: ActorAIResponse;
  error?: string;
}

/**
 * Parameters for building payload
 */
export interface BuildPayloadParams {
  name: string;
  audienceId: string;
  audienceName: string;
  audienceDescription: string;
  processedPrompt: string;
  sourceData: CompleteSourceData;
  userId: string;
  projectId: string;
}

// =============================================================================
// SERVICE
// =============================================================================

/**
 * ActorAIService
 *
 * Service for creating actors via AI webhook through backend proxy.
 */
export class ActorAIService {
  private webhookSettingsService: WebhookSettingsService;

  constructor() {
    this.webhookSettingsService = new WebhookSettingsService();
  }

  // ===========================================================================
  // BUILD SOURCE DATA
  // ===========================================================================

  /**
   * Build complete source data for AI submission
   *
   * Takes hydrated data from CompositeDataPicker and adds product info.
   * Also strips unnecessary fields like fullInterviewResult.
   */
  buildCompleteSourceData(
    hydratedData: HydratedSourceData,
    project?: ProjectData | null
  ): CompleteSourceData {
    // Process audiences to strip fullInterviewResult from actors
    const processedAudiences = hydratedData.audiences.map((audience) => {
      const audienceWithActors = audience as any;
      if (audienceWithActors.actors) {
        return {
          ...audience,
          actors: audienceWithActors.actors.map((actor: any) => {
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            const { fullInterviewResult, ...actorWithoutInterview } = actor;
            return actorWithoutInterview;
          }),
        };
      }
      return audience;
    });

    return {
      audiences: processedAudiences as any,
      tasks: hydratedData.tasks,
      studyCases: [], // CompositeDataPicker doesn't include studyCases yet
      product: project
        ? {
            projectName: project.projectName,
            description: project.description,
            toneOfVoice: project.toneOfVoice,
            designDescription: project.designDescription,
          }
        : null,
    };
  }

  // ===========================================================================
  // BUILD PAYLOAD
  // ===========================================================================

  /**
   * Build webhook payload for AI actor creation
   */
  buildPayload(params: BuildPayloadParams): ActorAIRequest {
    const {
      name,
      audienceId,
      audienceName,
      audienceDescription,
      processedPrompt,
      sourceData,
      userId,
      projectId,
    } = params;

    return {
      name: name.trim(),
      audienceName,
      audienceDescription,
      audienceId,
      prompt: processedPrompt,
      sourceData,
      userId,
      projectId,
      node_env: getCurrentEnvironment(),
    };
  }

  // ===========================================================================
  // CREATE ACTOR
  // ===========================================================================

  /**
   * Create actor via AI webhook
   *
   * Posts to backend proxy which forwards to the AI webhook.
   * Returns immediately with payload_sent status - actual actor creation
   * is tracked via TaskContext/Firestore listeners.
   */
  async createActor(request: ActorAIRequest): Promise<ActorAIServiceResult> {
    try {
      const backendUrl = getBackendUrl();
      const webhookUrls = await this.webhookSettingsService.getWebhookUrls();

      if (!webhookUrls?.aiActor) {
        return {
          success: false,
          error: 'AI Actor webhook URL not configured. Please check webhook settings.',
        };
      }

      const proxyRequest: WebhookProxyRequest = {
        url: webhookUrls.aiActor,
        payload: request,
        action_description: `Creating AI actor: ${request.name}`,
      };

      console.log('[ActorAIService] Sending request to webhook proxy:', {
        url: proxyRequest.url,
        action: proxyRequest.action_description,
      });

      const response = await fetch(`${backendUrl}/api/webhook-proxy`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(proxyRequest),
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('[ActorAIService] HTTP error:', response.status, errorText);
        return {
          success: false,
          error: `Failed to create actor: ${response.status} - ${errorText}`,
        };
      }

      const data: ActorAIResponse = await response.json();
      console.log('[ActorAIService] Success:', data);

      return {
        success: true,
        data,
      };
    } catch (error) {
      console.error('[ActorAIService] Error:', error);

      if (error instanceof TypeError) {
        return {
          success: false,
          error: 'Network error. Please check your internet connection.',
        };
      }

      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error occurred',
      };
    }
  }

  // ===========================================================================
  // HELPERS
  // ===========================================================================

  /**
   * Find audience by ID
   */
  getAudienceById(audienceId: string, audiences: Audience[]): Audience | undefined {
    return audiences.find((aud) => aud.id === audienceId);
  }
}

// =============================================================================
// SINGLETON INSTANCE
// =============================================================================

/**
 * Default service instance
 */
export const actorAIService = new ActorAIService();
